import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';

export interface RenewedCertificate {
  id: string;
  licenseId: string;
  licenseNumber: string;
  coaterName?: string;
  productionSiteName?: string;
  city?: string;
  currentExpiryDate?: Date;
  newExpiryDate?: Date;
  wasExtended: boolean;
  certificateSent: boolean;
  certificateSentOn?: Date;
  certificateGenerationStatus?: string; // "Pending", "Processing", "Completed", "Failed"
  glNotificationStatus?: string; // "Sent", "Failed", "Skipped", "NotApplicable"
  glName?: string;
  glEmail?: string;
  selected?: boolean;
}

@Component({
  selector: 'app-renewed-certificates-table',
  standalone: true,
  imports: [CommonModule, TableModule, TagModule, ButtonModule, TooltipModule, CheckboxModule, FormsModule],
  template: `
    <div class="renewed-certificates-table">
      <p-table
        [value]="certificates()"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        dataKey="id"
        class="p-datatable-striped"
        [selection]="selectedCertificates"
        (selectionChange)="onSelectionChange($event)">
        <ng-template #caption>
          <div class="flex justify-content-between align-items-center">
            <span class="text-xl font-semibold">Renewed Certificates</span>
            <span class="text-primary">
              {{ selectedCount() }} of {{ certificates().length }} selected for GL notification
            </span>
          </div>
        </ng-template>

        <ng-template #header>
          <tr>
            <th style="width: 3rem">
              <p-checkbox
                [binary]="true"
                [ngModel]="allSelected()"
                (onChange)="toggleAll($event)"
                [disabled]="!enableSelection()">
              </p-checkbox>
            </th>
            <th>License Number</th>
            <th>Coater</th>
            <th>Production Site</th>
            <th>Previous Expiry</th>
            <th>New Expiry</th>
            <th>GL Contact</th>
            <th>Status</th>
          </tr>
        </ng-template>

        <ng-template #body let-certificate>
          <tr>
            <td>
              <p-checkbox
                [binary]="true"
                [(ngModel)]="certificate.selected"
                (onChange)="onCertificateToggle(certificate)"
                [disabled]="!enableSelection() || certificate.certificateSent">
              </p-checkbox>
            </td>
            <td class="font-semibold">{{ certificate.licenseNumber }}</td>
            <td>
              <div [pTooltip]="certificate.coaterName" [tooltipDisabled]="(certificate.coaterName?.length || 0) <= 50">
                {{ certificate.coaterName || '' | slice: 0 : 50
                }}{{ (certificate.coaterName?.length || 0) > 50 ? '...' : '' }}
              </div>
              <small class="text-500">{{ certificate.city }}</small>
            </td>
            <td>{{ certificate.productionSiteName }}</td>
            <td>
              @if (certificate.currentExpiryDate) {
                <span [class.text-red-600]="isExpiringSoon(certificate.currentExpiryDate)">
                  {{ certificate.currentExpiryDate | date: 'shortDate' }}
                </span>
              }
            </td>
            <td class="font-semibold text-green-600">
              {{ certificate.newExpiryDate | date: 'shortDate' }}
            </td>
            <td>
              @if (certificate.glName) {
                <div>{{ certificate.glName }}</div>
                @if (certificate.glEmail) {
                  <small class="text-500">{{ certificate.glEmail }}</small>
                } @else {
                  <small class="text-orange-500">No contact email</small>
                }
              } @else {
                <span class="text-orange-500">No GL assigned</span>
              }
            </td>
            <td>
              @if (certificate.glNotificationStatus === 'Sent') {
                <p-tag severity="success" value="GL Notified"></p-tag>
                @if (certificate.certificateSentOn) {
                  <div class="text-xs text-500 mt-1">
                    {{ certificate.certificateSentOn | date: 'short' }}
                  </div>
                }
              } @else if (certificate.certificateGenerationStatus === 'Completed') {
                <p-tag severity="info" value="Ready"></p-tag>
              } @else if (certificate.certificateGenerationStatus === 'Processing') {
                <p-tag severity="warn" value="Generating..."></p-tag>
              } @else if (certificate.certificateGenerationStatus === 'Failed') {
                <p-tag severity="danger" value="Generation Failed"></p-tag>
              } @else {
                <p-tag severity="secondary" value="Not Extended"></p-tag>
              }
            </td>
          </tr>
        </ng-template>

        <ng-template #emptymessage>
          <tr>
            <td colspan="8" class="text-center p-4">No renewed certificates found.</td>
          </tr>
        </ng-template>

        <ng-template #summary>
          <div class="flex justify-content-between align-items-center p-3 surface-100 border-round">
            <div>
              <span class="font-semibold">Total Renewed: </span>
              <span class="text-primary font-bold">{{ renewedCount() }}</span>
            </div>
            @if (notifiedCount() > 0) {
              <div>
                <span class="font-semibold">Already Notified: </span>
                <span class="text-green-600 font-bold">{{ notifiedCount() }}</span>
              </div>
            }
            <div>
              <span class="font-semibold">Pending Notification: </span>
              <span class="text-orange-600 font-bold">{{ pendingCount() }}</span>
            </div>
          </div>
        </ng-template>
      </p-table>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host ::ng-deep {
        .p-datatable .p-datatable-thead > tr > th {
          background-color: var(--surface-50);
          color: var(--text-color);
          font-weight: 600;
          font-size: 0.875rem;
          padding: 0.5rem 0.75rem;
          border-bottom: 2px solid var(--surface-border);
        }

        .p-datatable .p-datatable-tbody > tr > td {
          font-size: 0.875rem;
          padding: 0.5rem 0.75rem;
        }

        .p-datatable .p-datatable-tbody > tr:hover {
          background-color: var(--surface-hover);
        }

        .p-datatable-striped .p-datatable-tbody > tr:nth-child(odd) {
          background-color: var(--surface-50);
        }
      }
    `,
  ],
})
export class RenewedCertificatesTableComponent {
  certificates = input.required<RenewedCertificate[]>();
  enableSelection = input<boolean>(true);

  selectionChanged = output<RenewedCertificate[]>();

  selectedCertificates: RenewedCertificate[] = [];

  selectedCount() {
    return this.certificates().filter((c) => c.selected && !c.certificateSent).length;
  }

  renewedCount() {
    return this.certificates().filter((c) => c.wasExtended).length;
  }

  notifiedCount() {
    return this.certificates().filter((c) => c.certificateSent).length;
  }

  pendingCount() {
    return this.certificates().filter((c) => c.wasExtended && !c.certificateSent).length;
  }

  allSelected() {
    const selectableCerts = this.certificates().filter((c) => !c.certificateSent && c.wasExtended);
    return selectableCerts.length > 0 && selectableCerts.every((c) => c.selected);
  }

  toggleAll(event: CheckboxChangeEvent) {
    const selected = event.checked;
    this.certificates().forEach((cert) => {
      if (!cert.certificateSent && cert.wasExtended) {
        cert.selected = selected;
      }
    });
    this.emitSelection();
  }

  onCertificateToggle(_certificate: RenewedCertificate) {
    this.emitSelection();
  }

  onSelectionChange(selection: RenewedCertificate[]) {
    this.selectedCertificates = selection;
    this.emitSelection();
  }

  private emitSelection() {
    const selected = this.certificates().filter((c) => c.selected);
    this.selectionChanged.emit(selected);
  }

  isExpiringSoon(date: Date): boolean {
    const expiry = new Date(date);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30;
  }
}
